/**
 * 設定画面：スキンタイプ（§4.8）、観測高モード（§5.3）、Push購読、
 * データ全削除（§11.3）、About（CC BY表示、§6.5）。
 */
import {
  areaSizeLabel,
  AUTO_START_DISTANCE_M,
  MED_SED,
  OBSERVER_HEIGHTS,
  TERRAIN_AREAS,
} from "@uvk/shared";
import type { HeightMode, UserSettings } from "@uvk/shared";
import { activeArea, routePrefs, saveLocalPrefs, saveSettings, state } from "../state.js";
import { deleteMe, subscribePush } from "../api.js";
import { wipeLocal } from "../db.js";
import { esc } from "../ui.js";
import { accountCardHtml, bindAccount } from "./account.js";

// VAPID公開鍵（デプロイ時に環境変数で差し込み。未設定ならPush購読ボタンを無効化）
const VAPID_PUBLIC_KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? "";

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

export function renderSettings(el: HTMLElement): void {
  const s = state.settings;
  el.innerHTML = `
    ${accountCardHtml()}
    <div class="card">
      <h2>👤 スキンタイプ <span class="sub">日本人はII〜IVが大半です</span></h2>
      <select id="skin">
        ${([1, 2, 3, 4] as const)
          .map(
            (t) =>
              `<option value="${t}" ${s.skinType === t ? "selected" : ""}>タイプ${["I（すぐ赤くなる）", "II（赤くなりやすい）", "III（少し赤くなる）", "IV（赤くなりにくい）"][t - 1]}　MED ${MED_SED[t]} SED</option>`,
          )
          .join("")}
      </select>
      <label class="field">身長（日かげの判定に利用）</label>
      <select id="height">
        ${(Object.keys(OBSERVER_HEIGHTS) as HeightMode[])
          .map(
            (m) =>
              `<option value="${m}" ${s.heightMode === m ? "selected" : ""}>${m === "adult" ? "150cm以上" : "150cm未満（ベビーカー・車いす）"}</option>`,
          )
          .join("")}
      </select>
    </div>

    <div class="card">
      <h2>🗺️ 地形データのエリア</h2>
      <p class="sub">
        日陰の計算に使う建物データのエリアです。ルート検索はこの範囲の中だけで動きます
        （経路探索が1枚のタイルの中で完結する作りのため、同時に1エリアだけ選べます）。
      </p>
      <select id="area">
        ${TERRAIN_AREAS.map(
          (a) =>
            `<option value="${a.id}" ${activeArea().id === a.id ? "selected" : ""}>${esc(a.name)}（${areaSizeLabel(a)}）</option>`,
        ).join("")}
      </select>
    </div>

    <div class="card">
      <h2>🚶 日陰ルート検索の上限</h2>
      <p class="sub">日陰重視ルートが最短ルートよりどれだけ余分に時間をかけてよいかの上限です</p>
      <label class="field">日陰①：移動時間の倍率上限（例 1.5 = 最短の1.5倍まで）</label>
      <input type="number" id="route-ratio" min="1" max="3" step="0.1" value="${routePrefs().maxTimeRatio}" />
      <label class="field">日陰②：追加時間の上限（分）</label>
      <input type="number" id="route-extra" min="0" max="60" step="1" value="${routePrefs().maxExtraMinutes}" />
    </div>

    <div class="card">
      <h2>🏠 自動外出モード</h2>
      <p class="sub">
        自宅を登録すると、アプリを開いた時に自宅から${AUTO_START_DISTANCE_M}m以上離れていれば
        計測を自動開始します。自宅の位置はこの端末の中だけに保存され、送信されません。
        ※アプリを閉じたままの自動開始はPWAの制約上できません（製品版で対応予定）
      </p>
      <div class="row">
        <button id="home-btn" class="ghost grow">${state.localPrefs.home ? "🏠 自宅を登録し直す" : "🏠 現在地を自宅として登録"}</button>
        <label><input type="checkbox" id="auto-start" ${state.localPrefs.autoStart ? "checked" : ""} ${state.localPrefs.home ? "" : "disabled"} /> 自動開始</label>
      </div>
      ${state.localPrefs.home ? `<p class="sub">✅ 自宅登録済み</p>` : ""}
    </div>

    <div class="card">
      <h2>🔔 予定アラート通知</h2>
      <p class="sub">屋外予定の10分前に、アプリを閉じていてもお知らせします</p>
      <button id="push-btn" class="ghost" ${VAPID_PUBLIC_KEY ? "" : "disabled"}>
        ${VAPID_PUBLIC_KEY ? "通知を有効にする" : "（サーバ設定待ち）"}
      </button>
    </div>

    <div class="card">
      <h2>🗑️ データの削除</h2>
      <p class="sub">サーバ上の集計データと、この端末内のすべての記録（軌跡・塗布記録）を削除します。軌跡はもともと端末の外に出ません。</p>
      <button id="delete-btn" class="ghost" style="color:var(--warn)">すべて削除する</button>
    </div>

    <div class="card">
      <h2>ℹ️ このアプリについて</h2>
      <p class="sub">
        UVかけいぼは、GPS軌跡×PLATEAU 3D建物×雲補正UV×日焼け止め減衰から、
        実際に肌へ受けた紅斑紫外線量（SED）を概算するアプリです。
        医療機器ではなく、疾病の予防・診断を目的としません。
        値は概算であり、不確かなときは多めに見積もる設計です。
      </p>
      <p class="sub">
        Weather data by <a href="https://open-meteo.com/" target="_blank" rel="noreferrer">Open-Meteo.com</a> (CC BY 4.0)<br/>
        建物形状：国土交通省 PLATEAU（3D都市モデル）<br/>
        地図：© OpenStreetMap contributors
      </p>
    </div>
  `;

  bindAccount(el, () => renderSettings(el));

  el.querySelector<HTMLSelectElement>("#skin")!.onchange = (ev) => {
    const skinType = Number((ev.target as HTMLSelectElement).value) as UserSettings["skinType"];
    void saveSettings({ ...state.settings, skinType });
  };
  el.querySelector<HTMLSelectElement>("#height")!.onchange = async (ev) => {
    const heightMode = (ev.target as HTMLSelectElement).value as HeightMode;
    await saveSettings({ ...state.settings, heightMode });
    // 観測高ごとに別のタイルなので、エリア切替と同じくリロードして読み直す。
    // これが無いと切替後のモードのタイルが未取得のままになり、範囲内の地点まで
    // 「範囲外」と表示されて日陰ルートが1本も出なくなる（実際に起きた）。
    location.reload();
  };

  el.querySelector<HTMLSelectElement>("#area")!.onchange = async (ev) => {
    const areaId = (ev.target as HTMLSelectElement).value;
    await saveLocalPrefs({ ...state.localPrefs, areaId });
    // タイルを読み直すためリロードする（経路の状態も作り直したい）
    location.reload();
  };

  const ratioInput = el.querySelector<HTMLInputElement>("#route-ratio")!;
  const extraInput = el.querySelector<HTMLInputElement>("#route-extra")!;
  const saveRoutePrefs = () => {
    const maxTimeRatio = Math.min(3, Math.max(1, Number(ratioInput.value) || 1.5));
    const maxExtraMinutes = Math.min(60, Math.max(0, Number(extraInput.value) || 0));
    void saveSettings({ ...state.settings, routePrefs: { maxTimeRatio, maxExtraMinutes } });
  };
  ratioInput.onchange = saveRoutePrefs;
  extraInput.onchange = saveRoutePrefs;

  el.querySelector<HTMLButtonElement>("#home-btn")!.onclick = () => {
    navigator.geolocation.getCurrentPosition(
      (p) => {
        void saveLocalPrefs({
          ...state.localPrefs,
          home: { lat: p.coords.latitude, lon: p.coords.longitude },
        });
        alert("現在地を自宅として登録しました（端末内のみに保存）");
      },
      () => alert("位置情報を取得できませんでした"),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };
  el.querySelector<HTMLInputElement>("#auto-start")!.onchange = (ev) => {
    void saveLocalPrefs({ ...state.localPrefs, autoStart: (ev.target as HTMLInputElement).checked });
  };

  el.querySelector<HTMLButtonElement>("#push-btn")!.onclick = async () => {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return alert("通知が許可されませんでした");
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
    });
    const ok = await subscribePush(sub);
    alert(ok ? "通知を有効にしました" : "サーバへの登録に失敗しました");
  };

  el.querySelector<HTMLButtonElement>("#delete-btn")!.onclick = async () => {
    if (!confirm("サーバと端末のデータをすべて削除します。よろしいですか？")) return;
    await deleteMe();
    await wipeLocal();
    location.reload();
  };
}
